import React, { useEffect, useState } from "react";
import { Users, X, UserPlus, Trash2, ShieldCheck, Crown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Friends() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/friends")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/friends", { name });
      toast.success(`Added ${name} to your friends!`);
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (fid) => {
    try {
      await api.delete(`/friends/${fid}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <>
      <button data-testid="open-friends" onClick={() => setOpen(true)} className="osrs-btn px-3 py-2 text-sm flex items-center gap-2">
        <Users size={16} /> <span>Friends</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-6 w-full max-w-xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="friends-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="osrs-title text-2xl flex items-center gap-2"><Users size={22} /> Friends</h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-friends"><X size={16} /></button>
            </div>

            <form onSubmit={add} className="flex gap-2 mb-4">
              <input className="osrs-input flex-1 px-3 py-2" placeholder="Add a friend by display name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="friend-name-input" />
              <button type="submit" disabled={busy} className="osrs-btn osrs-btn-green px-4 py-2 text-sm flex items-center gap-1" data-testid="add-friend-btn">
                <UserPlus size={16} /> Add
              </button>
            </form>

            {loading ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8">Loading...</p>
            ) : (
              <div className="overflow-x-auto" data-testid="friends-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="osrs-shadow text-[#E6A519] text-xs uppercase text-left">
                      <th className="py-2">Adventurer</th>
                      <th className="text-right">Points</th>
                      <th className="text-right">Done</th>
                      <th className="text-right"><ShieldCheck size={12} className="inline" title="Verified completions" /></th>
                      <th className="text-right"><Crown size={12} className="inline text-[#FFD700]" title="Season wins" /></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.user_id} data-testid={`friend-row-${r.user_id}`} className={`border-t border-[#5a4a2a] ${r.is_me ? "bg-[#2f4a1c]" : ""}`}>
                        <td className="py-2 osrs-shadow text-[#FFCF3F] font-bold"><span className="inline-flex items-center gap-2"><span title={r.online ? "Online now" : "Offline"} className={`inline-block w-2 h-2 rounded-full border border-black/50 ${r.online ? "bg-[#4ade80] shadow-[0_0_5px_#4ade80]" : "bg-[#6b6455]"}`} />{r.rsn ? (<a href={`/u/${encodeURIComponent(r.rsn)}`} target="_blank" rel="noreferrer" data-testid={`friend-link-${r.user_id}`} className="hover:underline decoration-dotted underline-offset-2" title={`View ${r.rsn}'s rank card`}>{r.name}</a>) : r.name}</span> {r.is_me && <span className="text-[#00FF00] text-xs">(you)</span>}</td>
                        <td className="text-right osrs-num text-[#FFCF3F]">{r.total_points}</td>
                        <td className="text-right osrs-num text-[#E6A519]">{r.completed}</td>
                        <td className="text-right osrs-num text-[#00FF00]">{r.verified}</td>
                        <td className="text-right osrs-num text-[#FFD700]">{r.season_wins}</td>
                        <td className="text-right">
                          {!r.is_me && (
                            <button onClick={() => remove(r.user_id)} className="osrs-btn osrs-btn-red px-2 py-1 text-xs" data-testid={`remove-friend-${r.user_id}`}><Trash2 size={12} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length <= 1 && (
                  <p className="osrs-shadow text-[#E6A519] text-center py-6 text-xs" data-testid="friends-empty">Add friends by their display name to compare points, completions and season wins.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
